import { Badge } from "@babackapp/customer-ui/components/badge";
import {
  LabeledButton,
  LabeledButtonContent,
  LabeledButtonLabel,
} from "@babackapp/customer-ui/components/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@babackapp/customer-ui/components/command";
import { Label } from "@babackapp/customer-ui/components/label";
import {
  Option,
  OptionAction,
  OptionContent,
  OptionDescription,
  OptionIcon,
  OptionTitle,
} from "@babackapp/customer-ui/components/option";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@babackapp/customer-ui/components/popover";
import { RadioGroupItem } from "@babackapp/customer-ui/components/radio-group";
import { CheckIcon, ChevronsUpDown, StoreIcon } from "lucide-react";
import { useState } from "react";
import { Controller, type Control } from "react-hook-form";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";
import { cn } from "@babackapp/customer-ui/lib/utils";
import { getIframeSource } from "../utils/get-iframe-source";
import { searchStores } from "../utils/store-selection";

type Store = {
  id?: string | null;
  name?: string | null;
  address?: { address1?: string | null; city?: string | null } | null;
  embedMap?: string | null;
};

type FormValues = {
  returnMode: string;
  pickupDate?: string;
  storeId?: string;
  isPickup?: boolean;
};

type Props = {
  control: Control<FormValues>;
  selected: boolean;
  selectableStores: Store[];
  /** A rule refuses the store: shown greyed out with its reason (BBS-418). */
  isRefused?: boolean;
  /** Basket answer still on its way: not selectable yet, no reason shown. */
  isPending?: boolean;
};

export function StoreOption({
  control,
  selected,
  selectableStores,
  isRefused = false,
  isPending = false,
}: Props) {
  const t = useCustomText();
  const isDisabled = isRefused || isPending;
  return (
    <Label htmlFor="store">
      <Option
        aria-disabled={isDisabled}
        className={cn(
          isRefused && "opacity-60",
          isDisabled && "cursor-not-allowed",
        )}
      >
        <OptionIcon>
          <StoreIcon />
        </OptionIcon>
        <OptionTitle>
          {t("returnMode.store.title")}
          {isRefused && <Badge>{t("returnMode.store.unavailable")}</Badge>}
        </OptionTitle>
        <OptionDescription>
          {isRefused
            ? t("returnMode.store.unavailableDescription")
            : t("returnMode.store.description")}
        </OptionDescription>
        <OptionAction>
          <RadioGroupItem value="store" id="store" disabled={isDisabled} />
        </OptionAction>
        <OptionContent>
          {selected && (
            <Controller
              control={control}
              name="storeId"
              render={({ field }) => (
                <StoreCombobox
                  value={field.value}
                  onChange={field.onChange}
                  selectableStores={selectableStores}
                  t={t}
                />
              )}
            />
          )}
        </OptionContent>
      </Option>
    </Label>
  );
}

type StoreComboboxProps = {
  value: string | undefined;
  onChange: (value: string) => void;
  selectableStores: Store[];
  t: (key: string) => string;
};

/** Map preview panel — every store's iframe mounts once and stays mounted for the
 * combobox's lifetime; switching stores only changes which layer is on top (z-index),
 * so hovering never triggers a reload/remount flicker. */
function StoreMapStack({
  stores,
  activeStoreId,
  showPlaceholder,
}: {
  stores: Store[];
  activeStoreId: string | null;
  showPlaceholder: boolean;
}) {
  return (
    <div className="relative h-48 w-full overflow-hidden bg-muted">
      <div
        className="absolute inset-0 flex items-center justify-center bg-muted"
        style={{ zIndex: showPlaceholder ? 10 : 0 }}
      >
        <StoreIcon className="size-6 text-muted-foreground" />
      </div>
      {stores
        .filter((store) => !!store.embedMap)
        .map((store) => {
          const src = getIframeSource(store.embedMap);
          if (!src) return null;
          const isActive = !showPlaceholder && store.id === activeStoreId;
          return (
            <iframe
              key={store.id}
              src={src}
              title={store.name ?? "map"}
              loading="lazy"
              className="absolute inset-0 h-full w-full border-0"
              style={{ zIndex: isActive ? 10 : 0 }}
            />
          );
        })}
    </div>
  );
}

function StoreCombobox({ value, onChange, selectableStores, t }: StoreComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [hoveredStoreId, setHoveredStoreId] = useState<string | null>(null);

  const hasAnyMap = selectableStores.some((s) => !!s.embedMap);
  const visibleStores = query.trim() ? searchStores(selectableStores, query) : selectableStores;

  const activeStoreId = hoveredStoreId ?? value ?? visibleStores[0]?.id ?? null;
  const activeStore = visibleStores.find((s) => s.id === activeStoreId) ?? null;
  const showPlaceholder = visibleStores.length === 0 || !activeStore?.embedMap;

  const handleSelect = (store: Store) => {
    onChange(store.id ?? "");
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <LabeledButton variant="outline">
          <LabeledButtonLabel>{t("returnMode.store.storeLabel")}</LabeledButtonLabel>
          <LabeledButtonContent>
            {value
              ? selectableStores.find((s) => s.id === value)?.name ||
                t("returnMode.store.storeSelected")
              : t("returnMode.store.noStoreSelected")}
          </LabeledButtonContent>
          <ChevronsUpDown />
        </LabeledButton>
      </PopoverTrigger>
      <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
        <div className="flex">
          {hasAnyMap && (
            <div className="w-32 shrink-0 border-r border-border sm:w-40">
              <StoreMapStack
                stores={selectableStores}
                activeStoreId={activeStoreId}
                showPlaceholder={showPlaceholder}
              />
            </div>
          )}
          <Command className="min-w-0 flex-1" shouldFilter={false}>
            <CommandInput
              value={query}
              onValueChange={setQuery}
              placeholder={t("returnMode.store.searchPlaceholder")}
            />
            <CommandList className="max-h-48">
              <CommandEmpty>{t("returnMode.store.noResults")}</CommandEmpty>
              <CommandGroup>
                {visibleStores.map((store) => (
                  <CommandItem
                    key={store.id}
                    value={store.id ?? ""}
                    onSelect={() => handleSelect(store)}
                    onMouseEnter={() => setHoveredStoreId(store.id ?? null)}
                  >
                    <CheckIcon
                      className={cn(
                        "shrink-0",
                        value === store.id ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <div className="min-w-0">
                      <div className="font-medium">{store.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {store.address?.address1}, {store.address?.city}
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </div>
      </PopoverContent>
    </Popover>
  );
}
